/**
 * LennyHistory — full Lenny (AI support) conversation history of one clinic,
 * across all its therapists. Grouped by day, newest day first; inside a day
 * messages run chronologically. Flagged answers (sent to the NiV review queue)
 * are marked.
 *
 * Data: collection-group read of therapists/{uid}/supportChat where clinicId
 * matches (NiV admin only, see firestore.rules).
 */

import { useMemo, useState } from "react";
import { Robot, User, Funnel, WarningCircle, Question } from "@phosphor-icons/react";
import ContentLoader from "../../../../components/shared/ContentLoader";
import { useGetClinicLennyHistoryQuery } from "../../../../store/services/nivApi";
import LennyText from "./LennyText";
import "./LennyHistory.css";

const HEB_DAYS = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];

const formatDayHeading = (dayKey) => {
  // dayKey = YYYY-MM-DD (Asia/Jerusalem, written by the Cloud Function)
  if (!dayKey) return "";
  const [y, m, d] = dayKey.split("-").map(Number);
  if (!y || !m || !d) return dayKey;
  const date = new Date(y, m - 1, d);
  return `יום ${HEB_DAYS[date.getDay()]}, ${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
};

const formatTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

function LennyHistory({ clinicId }) {
  const { data: messages = [], isLoading, isError } = useGetClinicLennyHistoryQuery(
    { clinicId },
    { skip: !clinicId }
  );
  const [therapistFilter, setTherapistFilter] = useState("all");
  const [flaggedOnly, setFlaggedOnly] = useState(false);

  const therapists = useMemo(() => {
    const map = new Map();
    messages.forEach((m) => {
      if (m.therapistId && !map.has(m.therapistId)) {
        map.set(m.therapistId, m.therapistName || "מטפל/ת");
      }
    });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [messages]);

  const stats = useMemo(() => {
    const questions = messages.filter((m) => m.role === "user").length;
    const flagged = messages.filter((m) => m.flagged).length;
    return { questions, flagged };
  }, [messages]);

  // Group by day (newest first); within a day chronological. When filtering
  // "flagged only", keep the user question that precedes each flagged answer.
  const days = useMemo(() => {
    let list = messages;
    if (therapistFilter !== "all") {
      list = list.filter((m) => m.therapistId === therapistFilter);
    }
    if (flaggedOnly) {
      const keep = new Set();
      const asc = [...list].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      asc.forEach((m, i) => {
        if (!m.flagged) return;
        keep.add(m.id);
        // preceding user message from the same therapist
        for (let j = i - 1; j >= 0; j -= 1) {
          if (asc[j].therapistId === m.therapistId) {
            if (asc[j].role === "user") keep.add(asc[j].id);
            break;
          }
        }
      });
      list = list.filter((m) => keep.has(m.id));
    }

    const byDay = new Map();
    list.forEach((m) => {
      const key = m.dayKey || (m.createdAt ? m.createdAt.slice(0, 10) : "");
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key).push(m);
    });
    return [...byDay.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([dayKey, items]) => ({
        dayKey,
        items: items.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
      }));
  }, [messages, therapistFilter, flaggedOnly]);

  if (isLoading) {
    return (
      <ContentLoader message="טוען שיחות…" />
    );
  }

  if (isError) {
    return <div className="nlh-state">לא הצלחנו לטעון את היסטוריית לני.</div>;
  }

  return (
    <div className="nlh-wrap">
      <div className="nlh-toolbar">
        <div className="nlh-stats">
          <span className="nlh-stat">
            <Robot size={14} weight="fill" />
            {stats.questions} שאלות
          </span>
          {stats.flagged > 0 && (
            <span className="nlh-stat nlh-stat-flagged">
              <WarningCircle size={14} weight="fill" />
              {stats.flagged} סומנו לבדיקה
            </span>
          )}
        </div>
        <div className="nlh-filters">
          {therapists.length > 1 && (
            <label className="nlh-select">
              <Funnel size={14} />
              <select
                value={therapistFilter}
                onChange={(e) => setTherapistFilter(e.target.value)}
              >
                <option value="all">כל המטפלים</option>
                {therapists.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="nlh-toggle">
            <input
              type="checkbox"
              checked={flaggedOnly}
              onChange={(e) => setFlaggedOnly(e.target.checked)}
            />
            רק שסומנו לבדיקה
          </label>
        </div>
      </div>

      {days.length === 0 ? (
        <div className="nlh-state nlh-empty">
          <Robot size={32} weight="light" />
          {messages.length === 0
            ? "הקליניקה עדיין לא שוחחה עם לני"
            : "אין הודעות שתואמות לסינון"}
        </div>
      ) : (
        <div className="nlh-days">
          {days.map(({ dayKey, items }) => (
            <section key={dayKey} className="nlh-day">
              <div className="nlh-day-head">{formatDayHeading(dayKey)}</div>
              <div className="nlh-messages">
                {items.map((m) => {
                  const isUser = m.role === "user";
                  return (
                    <div
                      key={m.id}
                      className={`nlh-msg ${isUser ? "nlh-msg-user" : "nlh-msg-assistant"} ${
                        m.flagged ? "nlh-msg-flagged" : ""
                      }`}
                    >
                      <div className="nlh-msg-avatar">
                        {isUser ? <User size={14} weight="fill" /> : <Robot size={14} weight="fill" />}
                      </div>
                      <div className="nlh-msg-body">
                        <div className="nlh-msg-head">
                          <span className="nlh-msg-name">
                            {isUser ? m.therapistName || "מטפל/ת" : "לני"}
                          </span>
                          {m.flagged && (
                            <span
                              className={`nlh-flag ${
                                m.covered === false ? "nlh-flag-unknown" : "nlh-flag-attention"
                              }`}
                            >
                              {m.covered === false ? (
                                <Question size={11} weight="fill" />
                              ) : (
                                <WarningCircle size={11} weight="fill" />
                              )}
                              {m.covered === false
                                ? "לני לא ידע"
                                : `דורש מנהל · ${m.adminAttention ?? ""}%`}
                            </span>
                          )}
                          <span className="nlh-msg-time" dir="ltr">
                            {formatTime(m.createdAt)}
                          </span>
                        </div>
                        <LennyText content={m.content} className="nlh-msg-text" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

export default LennyHistory;
